import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useCurrentCompany } from 'src/hooks/api/companies';
import { usePermissions } from 'src/modules/shared/contexts/permissions-context';
import { useSupportTenant } from 'src/modules/shared/contexts/support-tenant-context';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import { useApiRetryPolicy } from 'src/modules/shared/hooks/api/use-api-retry-policy';
import { useClientType } from 'src/modules/shared/hooks/use-client-type';
import type { PropertyPreviewAccessContext } from 'src/modules/shared/types/api/property-preview.type';
import {
  FilePermission,
  PropertyPermission,
} from 'src/modules/shared/types/permissions.type';
import {
  propertyPreviewQueryKey,
  requestPropertyPreviews,
} from 'src/modules/shared/utils/api/property-previews.util';
import { isApiAuthorizationError } from 'src/modules/shared/utils/api/query-error-policy.util';
import ErrorReporter from 'src/monitoring/errorReporter';
import type {
  PropertyPreviewContextValue,
  PropertyPreviewImageRefreshAttempt,
  PropertyPreviewProviderProps,
} from '../types/property-preview.type';
import { createPropertyPreviewCoordinator } from '../utils/property-preview-coordinator.util';
import { createPropertyPreviewVisibilityRegistry } from '../utils/property-preview-visibility.util';
import { PropertyPreviewContext } from './property-preview-context';

/**
 * Owns one communications-view scheduler across board, list, and drag observers.
 * Requests wait for the existing company, auth, support and permission providers.
 * @param props - Communications descendants.
 * @returns The shared access-scoped provider.
 */
export const PropertyPreviewProvider = ({
  children,
}: PropertyPreviewProviderProps) => {
  const queryClient = useQueryClient();
  const retryPolicy = useApiRetryPolicy();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth0();
  const { currentCompany, loading: companyLoading } = useCurrentCompany();
  const clientType = useClientType();
  const { user: currentUser, loading: currentUserLoading } = useCurrentUser();
  const { permissions, loading: permissionsLoading } = usePermissions();
  const { assumedTenantId, loading: supportLoading } = useSupportTenant();
  const userId = user?.sub;
  const tenantId = assumedTenantId ?? currentUser?.tenantId;
  const permissionKey = [
    PropertyPermission.Read,
    PropertyPermission.ReadScoped,
    FilePermission.Read,
  ]
    .filter((permission) => permissions.includes(permission))
    .sort()
    .join('|');
  const eligible =
    !authLoading &&
    !companyLoading &&
    !currentUserLoading &&
    !permissionsLoading &&
    !supportLoading &&
    isAuthenticated &&
    clientType === 'REAL_ESTATE' &&
    currentCompany?.tenantId === tenantId &&
    Boolean(userId) &&
    Boolean(tenantId) &&
    permissions.includes(FilePermission.Read) &&
    (permissions.includes(PropertyPermission.Read) ||
      permissions.includes(PropertyPermission.ReadScoped));

  const access = useMemo<PropertyPreviewAccessContext | null>(() => {
    if (!eligible || !tenantId || !userId) return null;
    return { tenantId, userId, permissionKey };
  }, [eligible, permissionKey, tenantId, userId]);
  const accessKey = JSON.stringify([tenantId, userId, permissionKey, eligible]);
  const currentAccessKey = useRef(accessKey);
  currentAccessKey.current = accessKey;
  const [resources, setResources] =
    useState<PropertyPreviewContextValue | null>(null);

  useLayoutEffect(() => {
    let mounted = true;
    const isCurrent = (): boolean =>
      mounted && currentAccessKey.current === accessKey;
    const visibility = createPropertyPreviewVisibilityRegistry();
    const imageRefreshes = new Map<
      number,
      PropertyPreviewImageRefreshAttempt
    >();
    const coordinator = createPropertyPreviewCoordinator({
      request: requestPropertyPreviews,
      retryPolicy,
      isCurrent,
      onPolicyError: ErrorReporter.sendException,
      onTerminalError: (error, propertyIds) => {
        if (!isCurrent()) return;
        if (access && isApiAuthorizationError(error)) {
          for (const propertyId of propertyIds) {
            const query = queryClient.getQueryCache().find({
              queryKey: propertyPreviewQueryKey(access, propertyId),
              exact: true,
            });
            query?.setState({ data: undefined, dataUpdatedAt: 0 });
          }
        }
        if (error instanceof Error) {
          return retryPolicy.onTerminalError(error, isCurrent);
        }
      },
    });

    const refreshImage = (propertyId: number, updatedAt: number): void => {
      if (!access || !isCurrent()) return;
      const existing = imageRefreshes.get(propertyId);
      if (
        existing &&
        (existing.originalUpdatedAt === updatedAt ||
          existing.refreshedUpdatedAt === updatedAt)
      ) {
        return;
      }

      const attempt: PropertyPreviewImageRefreshAttempt = {
        originalUpdatedAt: updatedAt,
      };
      imageRefreshes.set(propertyId, attempt);
      const queryKey = propertyPreviewQueryKey(access, propertyId);
      void queryClient
        .refetchQueries(
          { queryKey, exact: true, type: 'active' },
          { cancelRefetch: false }
        )
        .catch(ErrorReporter.sendException)
        .finally(() => {
          if (!isCurrent()) return;
          attempt.refreshedUpdatedAt =
            queryClient.getQueryState(queryKey)?.dataUpdatedAt;
        });
    };

    setResources({ access, accessKey, coordinator, visibility, refreshImage });
    return () => {
      mounted = false;
      coordinator.dispose();
      visibility.dispose();
      imageRefreshes.clear();
      if (access && currentAccessKey.current !== accessKey) {
        queryClient.removeQueries({
          queryKey: [
            'catalog/properties',
            'preview',
            access.tenantId,
            access.userId,
            access.permissionKey,
          ],
        });
      }
    };
  }, [access, accessKey, queryClient, retryPolicy]);

  const value = resources?.accessKey === accessKey ? resources : null;
  return (
    <PropertyPreviewContext.Provider value={value}>
      {children}
    </PropertyPreviewContext.Provider>
  );
};
